/**
 * Utility functions for sorting worklog groups and items
 * Extracted for testability and maintainability
 */

/**
 * Parses a Jira issue key (e.g., "PROJ-123") into prefix and number parts
 * @param {string} key - The Jira issue key
 * @returns {{prefix: string, num: number}} - Object with prefix and numeric part
 */
export const getJiraKeyParts = (key) => {
  if (typeof key !== 'string') {
    return { prefix: '', num: Number.NEGATIVE_INFINITY };
  }

  const match = key.match(/^(.*)-(\d+)$/);
  if (!match) {
    return { prefix: key, num: Number.NEGATIVE_INFINITY };
  }

  const [, prefix, numStr] = match;
  return { prefix, num: parseInt(numStr, 10) };
};

/**
 * Compares two Jira keys by prefix first, then by numeric value
 * @param {string} keyA - First Jira key
 * @param {string} keyB - Second Jira key
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
export const compareJiraKeys = (keyA, keyB) => {
  const aParts = getJiraKeyParts(keyA);
  const bParts = getJiraKeyParts(keyB);

  if (aParts.prefix !== bParts.prefix) {
    return aParts.prefix > bParts.prefix ? 1 : -1;
  }
  return aParts.num - bParts.num;
};

/**
 * Gets the sort value for a group based on the field
 * @param {string} field - The field to sort by
 * @param {object} group - The group object
 * @param {string} key - The group key
 * @param {string} groupingField - The current grouping field
 * @returns {*} - The value to sort by
 */
export const getGroupSortValue = (field, group, key, groupingField) => {
  switch (field) {
    case 'name':
      if (groupingField === 'issue' || groupingField === 'Parent') {
        const parts = getJiraKeyParts(key);
        // If num is NEGATIVE_INFINITY, the key didn't match the pattern, return as string
        if (parts.num === Number.NEGATIVE_INFINITY) {
          return key;
        }
        return { prefix: parts.prefix, num: parts.num };
      }
      return group.label || key;
    case 'totalHours':
      return group.totalHours || 0;
    case 'totalEstimated':
      return group.totalEstimated || 0;
    case 'uniqueIssues':
      return group.uniqueIssuesCount || 0;
    case 'totalEntries':
      return group.totalEntries || 0;
    case 'efficiency':
      return group.efficiency || 0;
    case 'variance':
      return group.variance || 0;
    case 'earliestDate':
      // earliestDate is already a Date object, use getTime() directly
      return group.earliestDate ? group.earliestDate.getTime() : Number.NEGATIVE_INFINITY;
    case 'latestDate':
      // latestDate is already a Date object, use getTime() directly
      return group.latestDate ? group.latestDate.getTime() : Number.NEGATIVE_INFINITY;
    default:
      return group.label || key;
  }
};

/**
 * Compares two group sort values
 * @param {*} aVal - First value
 * @param {*} bVal - Second value
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
export const compareGroupValues = (aVal, bVal, direction) => {
  // Handle Jira key comparison (object with prefix and num)
  if (typeof aVal === 'object' && aVal !== null && 'prefix' in aVal && 'num' in aVal &&
      typeof bVal === 'object' && bVal !== null && 'prefix' in bVal && 'num' in bVal) {
    if (aVal.prefix !== bVal.prefix) {
      const cmp = aVal.prefix > bVal.prefix ? 1 : -1;
      return direction === 'asc' ? cmp : -cmp;
    }
    const cmp = aVal.num - bVal.num;
    return direction === 'asc' ? cmp : -cmp;
  }

  // Standard comparison
  if (direction === 'asc') {
    return aVal > bVal ? 1 : -1;
  } else {
    return aVal < bVal ? 1 : -1;
  }
};

/**
 * Gets the sort value for a worklog item based on the field
 * @param {string} field - The field to sort by
 * @param {object} log - The worklog item
 * @returns {*} - The value to sort by
 */
export const getWorklogSortValue = (field, log) => {
  if (field === 'issue_key') {
    return getJiraKeyParts(log.issue_key);
  }

  let value = log[field];

  // Convert date/datetime fields to timestamps for proper comparison
  if (field === 'started') {
    // Use started field directly
    if (value) {
      return new Date(value).getTime();
    }
    // Fallback to loggedDate if started is not available
    if (log.loggedDate) {
      return new Date(log.loggedDate).getTime();
    }
    return Number.NEGATIVE_INFINITY;
  }
  
  // For loggedDate, use only loggedDate field (not started)
  if (field === 'loggedDate') {
    if (value) {
      return new Date(value).getTime();
    }
    return Number.NEGATIVE_INFINITY;
  }

  // Handle null/undefined values
  if (value == null) {
    return Number.NEGATIVE_INFINITY;
  }

  return value;
};

/**
 * Compares two worklog sort values
 * @param {*} aVal - First value
 * @param {*} bVal - Second value
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {number} - Comparison result (-1, 0, or 1)
 */
export const compareWorklogValues = (aVal, bVal, direction) => {
  // Handle Jira key comparison (object with prefix and num)
  if (typeof aVal === 'object' && aVal !== null && 'prefix' in aVal && 'num' in aVal &&
      typeof bVal === 'object' && bVal !== null && 'prefix' in bVal && 'num' in bVal) {
    if (aVal.prefix !== bVal.prefix) {
      const cmp = aVal.prefix > bVal.prefix ? 1 : -1;
      return direction === 'asc' ? cmp : -cmp;
    }
    const cmp = aVal.num - bVal.num;
    return direction === 'asc' ? cmp : -cmp;
  }

  // Standard comparison
  if (direction === 'asc') {
    return aVal > bVal ? 1 : -1;
  } else {
    return aVal < bVal ? 1 : -1;
  }
};

/**
 * Sorts groups by the specified field and direction
 * @param {Array} groups - Array of [key, group] tuples
 * @param {object} sortingConfig - Sorting configuration with field and direction
 * @param {string} groupingField - The current grouping field
 * @returns {Array} - Sorted array of [key, group] tuples
 */
export const sortGroups = (groups, sortingConfig, groupingField) => {
  const { field, direction } = sortingConfig;
  
  return [...groups].sort(([keyA, groupA], [keyB, groupB]) => {
    const aVal = getGroupSortValue(field, groupA, keyA, groupingField);
    const bVal = getGroupSortValue(field, groupB, keyB, groupingField);
    return compareGroupValues(aVal, bVal, direction);
  });
};

/**
 * Sorts worklogs within a group by the specified field and direction
 * @param {Array} logs - Array of worklog items
 * @param {object} sortingConfig - Sorting configuration with field and direction
 * @returns {Array} - Sorted array of worklog items
 */
export const sortWorklogs = (logs, sortingConfig) => {
  if (!Array.isArray(logs) || logs.length === 0) {
    return logs;
  }

  const { field, direction } = sortingConfig;
  
  return [...logs].sort((a, b) => {
    const aVal = getWorklogSortValue(field, a);
    const bVal = getWorklogSortValue(field, b);
    return compareWorklogValues(aVal, bVal, direction);
  });
};

